import type {
  CreateManualAbsenceInput,
  UpdateManualAbsenceInput,
} from "@msc/shared";

import { toLocalDateString } from "../../lib/date.js";

/**
 * Der Zustand des Erfassungsdialogs — und die Umrechnung in die Nutzlast.
 *
 * **Warum das hier steht und nicht in der Komponente.** Die Umrechnung ist die
 * einzige Stelle des Dialogs, an der man sich lautlos vertun kann: Mantines
 * `DatePickerInput` liefert ein `Date` zur LOKALEN Mitternacht, und
 * `toISOString()` schiebt den Tag in MESZ um eins zurück. Ein Eintrag „ab dem
 * 23.12." läge dann am 22.12. — ohne Fehlermeldung, nur um einen Tag daneben,
 * und beim Kunden steht ein falsches Datum. Als eigene Funktion ist sie
 * prüfbar, ohne einen Kalender anzuklicken.
 */
export interface AbsenceFormStand {
  employeeIds: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
  dayFraction: "1" | "0.5";
}

export const LEERES_ABSENCE_FORMULAR: AbsenceFormStand = {
  employeeIds: [],
  dateFrom: null,
  dateTo: null,
  dayFraction: "1",
};

/** Gemeinsamer Teil von Anlegen und Ändern: Zeitraum und Anteil. */
export function zeitraumEingabe(
  form: AbsenceFormStand,
): UpdateManualAbsenceInput | null {
  if (!form.dateFrom || !form.dateTo) return null;
  return {
    dateFrom: toLocalDateString(form.dateFrom),
    dateTo: toLocalDateString(form.dateTo),
    dayFraction: form.dayFraction === "0.5" ? 0.5 : 1,
  };
}

/** Nutzlast fürs Anlegen — ohne Person ist nichts zu erfassen. */
export function anlegeEingabe(
  form: AbsenceFormStand,
): CreateManualAbsenceInput | null {
  const zeitraum = zeitraumEingabe(form);
  if (!zeitraum || form.employeeIds.length === 0) return null;
  return { employeeIds: form.employeeIds, ...zeitraum };
}

/**
 * Das Ende folgt dem Anfang, solange es davor liegt. Ein Zeitraum, der mit
 * einer Fehlermeldung beginnt, ist unangenehmer als einer, der sich selbst
 * sortiert.
 */
export function endeNachziehen(
  form: AbsenceFormStand,
  dateFrom: Date | null,
): AbsenceFormStand {
  const dateTo =
    form.dateTo && dateFrom && form.dateTo < dateFrom ? dateFrom : form.dateTo;
  return { ...form, dateFrom, dateTo };
}
